import { Button, Flex, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconEdit as EditIcon } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { GovEvalMinStdDeleteButton } from '@/module/GovEvaluation/components/GovEvalMinStdDeleteButton';
import { GovEvaluationMinimalStandardModalForm } from '@/module/GovEvaluation/form/GovEvaluationMinimalStandardModalForm';

import { Content } from '@/core/components';

import { FileCard, Id, JobTitle, formatDecimal, useAppNotification } from '@feature/utils';

import { useUpdateMinimalStandard } from '../model/GovEvaluationQuery';
import { GovEvalMinimalStandardRequest, GovMinimalStandard } from '../model/GovEvaluationTypes';

interface GovEvaluationMinimalStandardProps {
  data: GovMinimalStandard | undefined;

  canEdit: boolean;
  authorId: Id;
  evaluationId: Id;

  jobTitle: JobTitle;
}

const onDownloadEditable = (evaluationId: Id, authorId: Id) => {
  window.open(`/api/gov-evaluation/${evaluationId}/author/${authorId}/download-min-std-editable`);
};

const onDownloadSigned = (evaluationId: Id, authorId: Id) => {
  window.open(`/api/gov-evaluation/${evaluationId}/author/${authorId}/download-min-std-signed`);
};

export function GovEvaluationAuthorMinimalStandard(props: GovEvaluationMinimalStandardProps) {
  const { evaluationId, data, canEdit, authorId, jobTitle } = props;
  const { t } = useTranslation();
  const [formOpened, { open: openForm, close: closeForm }] = useDisclosure(false);
  const { isPending, mutate } = useUpdateMinimalStandard(evaluationId);

  const shouldUploadMinStd = jobTitle === 'CONF' || jobTitle === 'PROFESSOR' || jobTitle == 'CS1' || jobTitle === 'CS2';

  const notification = useAppNotification();
  const onSubmit = (values: GovEvalMinimalStandardRequest) => {
    mutate(values, {
      onSuccess: () => {
        notification.success();
        closeForm();
      },
      onError: notification.error,
    });
  };

  return (
    <Content
      type="outline"
      bg="white"
    >
      <Flex
        align="center"
        justify="space-between"
      >
        <Flex
          direction="column"
          gap="10px"
        >
          <Stack gap="sm">
            {data?.totalScore && <Text fw="500">Punctaj total: {formatDecimal(data.totalScore)}p</Text>}
            {data?.notFulfilledList && data.notFulfilledList.length > 0 && (
              <Text>Criterii neîndeplinite: {data.notFulfilledList.join(', ')}</Text>
            )}
            <Text fw="500">{t('govEvaluation.editableMinStd')}</Text>
            {data == null && (
              <Text
                c="dimmed"
                fs="italic"
              >
                {t('unspecified')}
              </Text>
            )}
            {data?.editableProofFile != null && (
              <FileCard
                file={data.editableProofFile}
                onDownload={() => onDownloadEditable(evaluationId, authorId)}
              />
            )}
          </Stack>
          <Stack>
            <Text fw="500">{t('govEvaluation.signedMinStd')}</Text>
            {data == null && (
              <Text
                c="dimmed"
                fs="italic"
              >
                {t('unspecified')}
              </Text>
            )}
            {data?.proofFile != null && (
              <FileCard
                file={data.proofFile}
                onDownload={() => onDownloadSigned(evaluationId, authorId)}
              />
            )}
          </Stack>
        </Flex>
        <Stack>
          {shouldUploadMinStd ? (
            <Button
              onClick={openForm}
              disabled={!canEdit}
              leftSection={<EditIcon size="14px" />}
              size="xs"
            >
              {t('button.edit')}
            </Button>
          ) : (
            <Text
              size="sm"
              maw="200px"
              ta="center"
              fw="500"
              c="dimmed"
            >
              Funcția didactică selectată nu necesită încărcarea fișei CNATCU
            </Text>
          )}
          {data && (
            <GovEvalMinStdDeleteButton
              disabled={!canEdit}
              evaluationId={evaluationId}
            />
          )}
        </Stack>
      </Flex>
      {canEdit && formOpened && (
        <GovEvaluationMinimalStandardModalForm
          isSubmitting={isPending}
          onSubmit={onSubmit}
          onClose={closeForm}
        />
      )}
    </Content>
  );
}
